import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Snackbar,
  Alert as MuiAlert,
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { X } from "lucide-react";
import { LuPlus } from "react-icons/lu";
import { ClipLoader } from "react-spinners";
import axiosInstance from "../axiosinstance";

// ─── Helpers (copied from InvoiceForm) ───────────────────────────────────────

const FULL_DATE_REGEX = /^(\d{1,2})-(\d{1,2})-(\d{2}|\d{4})$/;

const normalizeBirthDateInput = (value, { allowPartial = true } = {}) => {
  if (!value) return { value: "", error: "" };

  let cleaned = value.trim().replace(/[./]/g, "-").replace(/-+/g, "-");

  if (cleaned.startsWith("-") || cleaned.endsWith("-"))
    return { value: cleaned, error: "Invalid date format" };

  if (/[^0-9-]/.test(cleaned))
    return { value: cleaned, error: "Only numbers allowed" };

  if (/^(\d{1,2})-(\d{5,})$/.test(cleaned))
    return { value: cleaned, error: "Invalid year format" };

  if (/^(\d{1,2})-(\d{3})-(\d{3})$/.test(cleaned))
    return { value: cleaned, error: "Invalid date format. Use DD-MM-YYYY" };

  if (/^(\d{3})-(\d{1,2})-(\d{2,4})$/.test(cleaned))
    return { value: cleaned, error: "Invalid day format. Use DD-MM-YYYY" };

  if (/^(\d{1,2})-(\d{1,2})-(\d{3})$/.test(cleaned))
    return { value: cleaned, error: "Enter full year (YYYY)" };

  if (/^(\d{1,2})-(\d{4})-(\d{1,2})$/.test(cleaned))
    return { value: cleaned, error: "Invalid format. Use DD-MM-YYYY" };

  if (/^(\d{4})-(\d{1,2})-(\d{1,2})$/.test(cleaned))
    return { value: cleaned, error: "Invalid format. Use DD-MM-YYYY" };

  const possibleInvalidYear = /^(\d{2})-(\d{1,2})-(\d{2})$/;
  if (possibleInvalidYear.test(cleaned)) {
    const match = cleaned.match(possibleInvalidYear);
    if (parseInt(match[1], 10) > 12 && parseInt(match[3], 10) <= 31)
      return { value: cleaned, error: "Enter full year (YYYY)" };
  }

  if (/^(\d{1,2})-(\d{1,2})-(\d{1})$/.test(cleaned))
    return { value: cleaned, error: "Enter full year (YYYY)" };

  if (allowPartial && !FULL_DATE_REGEX.test(cleaned))
    return { value: cleaned, error: "" };

  const match = cleaned.match(FULL_DATE_REGEX);
  if (!match) return { value: cleaned, error: "Invalid date format" };

  let day = parseInt(match[1], 10);
  let month = parseInt(match[2], 10);
  const yearRaw = match[3];
  let year = parseInt(yearRaw, 10);
  if (yearRaw.length === 2) year = 2000 + year;

  if (month < 1 || month > 12)
    return { value: cleaned, error: "Month must be between 1–12" };
  if (day < 1 || day > 31)
    return { value: cleaned, error: "Day must be between 1–31" };
  if (day === 0 || month === 0)
    return { value: cleaned, error: "Day and month cannot be zero" };

  const jsDate = new Date(year, month - 1, day);
  if (
    jsDate.getFullYear() !== year ||
    jsDate.getMonth() + 1 !== month ||
    jsDate.getDate() !== day
  )
    return { value: cleaned, error: "Invalid calendar date" };

  if (year < 1935)
    return {
      value: cleaned,
      error: "Invalid year. Please enter a valid year (1935-2099)",
    };

  let age = new Date().getFullYear() - year;
  const monthDiff = new Date().getMonth() - (month - 1);
  const dayDiff = new Date().getDate() - day;
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) age--;

  const normalized = `${String(day).padStart(2, "0")}-${String(month).padStart(2, "0")}-${year}`;
  if (allowPartial) return { value: cleaned, error: "" };
  return { value: normalized, error: "" };
};

// ─── Sub-constants ────────────────────────────────────────────────────────────

const companyTypes = [
  { value: "individual", label: "Individual / Proprietorship" },
  { value: "huf", label: "HUF" },
  { value: "partnership_firm", label: "Partnership Firm" },
  { value: "private_ltd", label: "Private Limited Company" },
  { value: "limited_company", label: "Limited Company" },
  { value: "llp", label: "Limited Liability Partnership" },
  { value: "aop", label: "Association of Persons" },
  { value: "trust", label: "Trust" },
];

const balanceOptions = [
  { value: "advance", label: "Advance" },
  { value: "pending", label: "Pending" },
  { value: "Financially Clear", label: "Financially Clear" },
];

const getDefaultContact = () => ({
  name: "",
  email: "",
  phone: "",
  isPrimary: true,
  isFamilyPrimaryMapped: false,
});

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "1100px",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "12px",
  overflowY: "auto",
  zIndex: 1400,
};

const modalHeaderStyle = {
  p: 2,
  borderBottom: "1px solid #e0e0e0",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  bgcolor: "#f5f5f5",
};

const modalContentStyle = {
  p: 2,
  maxHeight: "calc(90vh - 140px)",
  overflow: "auto",
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function ClientModal({
  open,
  onClose,
  onClientCreated, // callback(newClientId) after successful creation
  setClients,      // to push new client into parent's list
}) {
  // ── form state ──────────────────────────────────────────────────────────────
  const [modalClientName, setModalClientName] = useState("");
  const [modalCompanyName, setModalCompanyName] = useState("");
  const [modalFileNumber, setModalFileNumber] = useState("");
  const [modalSelectedCompanyType, setModalSelectedCompanyType] = useState("");
  const [modalSelectedSubsidary, setModalSelectedSubsidary] = useState("");
  const [modalSubsidaries, setModalSubsidaries] = useState([]);
  const [modalPaymentTerm, setModalPaymentTerm] = useState(30);
  const [modalSelectedOptionBalance, setModalSelectedOptionBalance] = useState(null);
  const [modalDob, setModalDob] = useState("");
  const [modalDobError, setModalDobError] = useState("");
  const [modalPan, setModalPan] = useState("");
  const [modalOpeningAmount, setModalOpeningAmount] = useState("");
  const [modalAddress, setModalAddress] = useState({
    street: "",
    city: "",
    state: "",
    postalCode: "",
  });
  const [contactPersons, setContactPersons] = useState([getDefaultContact()]);
  const [clientType, setClientType] = useState("individual");

  // ── family group state ───────────────────────────────────────────────────────
  const [familyGroups, setFamilyGroups] = useState([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState("");
  const [familyGroupsLoading, setFamilyGroupsLoading] = useState(false);
  const [familyGroupsError, setFamilyGroupsError] = useState("");
  const [isCreateFamilyModalOpen, setIsCreateFamilyModalOpen] = useState(false);
  const [createFamilyForm, setCreateFamilyForm] = useState({
    family_name: "",
    contact_name: "",
    phone_number: "",
    email: "",
  });
  const [createFamilyErrors, setCreateFamilyErrors] = useState({});
  const [isCreatingFamily, setIsCreatingFamily] = useState(false);

  // ── ui / loading state ───────────────────────────────────────────────────────
  const [modalErrors, setModalErrors] = useState({});
  const [modalLoading, setModalLoading] = useState(false);
  const [modalIsSaved, setModalIsSaved] = useState(false);
  const [isModalSubmitting, setIsModalSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // ── snackbar ─────────────────────────────────────────────────────────────────
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const firstfield = useRef();
  const createFamilyFirstInputRef = useRef(null);
  const menuPortalTarget = typeof window !== "undefined" ? document.body : null;
  const familyModalPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  // ── fetch subsidiaries when modal opens ──────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    const fetchSubsidiaries = async () => {
      try {
        setModalLoading(true);
        const response = await axiosInstance.get(
          `/api/get_all_subsidiary?page_status=Active`
        );
        if (response.status === 200) {
          const firms = response.data.data;
          setModalSubsidaries(
            firms.map((company) => ({
              label: company.Subsidiary_name,
              value: company.subsidiary_id,
              tag_name: company.tag_name,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching subsidiary data:", error.message);
      } finally {
        setModalLoading(false);
      }
    };

    fetchSubsidiaries();
  }, [open]);
  
  // ── family group effects ─────────────────────────────────────────────────────
  useEffect(() => {
    if (clientType === "family group") {
      fetchFamilyGroups();
      return;
    }
    setSelectedFamilyId("");
    setFamilyGroupsError("");
    setIsCreateFamilyModalOpen(false);
  }, [clientType]);

  useEffect(() => {
    if (!isCreateFamilyModalOpen) return;
    const rafId = window.requestAnimationFrame(() => {
      createFamilyFirstInputRef.current?.focus({ preventScroll: true });
    });
    const timer = window.setTimeout(() => {
      createFamilyFirstInputRef.current?.focus({ preventScroll: true });
    }, 120);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timer);
    };
  }, [isCreateFamilyModalOpen]);

  useEffect(() => {
    if (!isCreateFamilyModalOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isCreateFamilyModalOpen]);

  // ── helpers ──────────────────────────────────────────────────────────────────
  const clearMappedFamilyContact = (contacts) => {
    const nonFamilyContacts = (contacts || [])
      .filter((c) => !c?.isFamilyPrimaryMapped)
      .map((c) => ({ ...c, isFamilyPrimaryMapped: false }));

    if (nonFamilyContacts.length === 0) return [getDefaultContact()];

    const hasPrimary = nonFamilyContacts.some((c) => c.isPrimary);
    return nonFamilyContacts.map((c, i) => ({
      ...c,
      isPrimary: hasPrimary ? c.isPrimary : i === 0,
    }));
  };

  const fetchFamilyGroups = async (force = false) => {
    if (!force && (familyGroupsLoading || familyGroups.length > 0)) return;
    try {
      setFamilyGroupsLoading(true);
      setFamilyGroupsError("");
      const response = await axiosInstance.get("/api/family_group");
      const familyGroupRows = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data?.result)
        ? response.data.result
        : Array.isArray(response.data)
        ? response.data
        : [];
      setFamilyGroups(familyGroupRows);
    } catch (error) {
      console.error("Error fetching family groups:", error.message);
      setFamilyGroupsError("Failed to load family groups");
    } finally {
      setFamilyGroupsLoading(false);
    }
  };

  const mapFamilyContactToPrimary = (family) => {
    if (!family) return;
    setContactPersons((prev) => {
      const nonFamilyContacts = (prev || [])
        .filter((c) => !c?.isFamilyPrimaryMapped)
        .map((c) => ({ ...c, isFamilyPrimaryMapped: false }));

      const mapped = {
        name: family.contact_name || "",
        email: family.email || "",
        phone: (family.phone_number || "").toString(),
        isPrimary: true,
        isFamilyPrimaryMapped: true,
      };

      return [mapped, ...nonFamilyContacts].map((c, i) => ({
        ...c,
        isPrimary: i === 0,
      }));
    });
  };

  const handleClientTypeChange = (nextClientType) => {
    setClientType(nextClientType);
    setErrors((prev) => ({ ...prev, clientType: "" }));
    if (nextClientType !== "individual") return;
    setSelectedFamilyId("");
    setFamilyGroupsError("");
    setIsCreateFamilyModalOpen(false);
    setContactPersons((prev) => clearMappedFamilyContact(prev));
  };

  // ── reset on close ───────────────────────────────────────────────────────────
  const handleClose = () => {
    setModalClientName("");
    setModalCompanyName("");
    setModalFileNumber("");
    setModalSelectedCompanyType("");
    setModalSelectedSubsidary("");
    setModalPaymentTerm(30);
    setModalSelectedOptionBalance(null);
    setModalDob("");
    setModalDobError("");
    setModalPan("");
    setModalOpeningAmount("");
    setModalAddress({ street: "", city: "", state: "", postalCode: "" });
    setContactPersons([getDefaultContact()]);
    setSelectedFamilyId("");
    setClientType("individual");
    setModalErrors({});
    setModalIsSaved(false);
    setIsModalSubmitting(false);
    setModalLoading(false);
    setErrors({});
    onClose();
  };

  // ── contact person handlers ──────────────────────────────────────────────────
  const handleModalAddContactPerson = () =>
    setContactPersons((prev) => [
      ...prev,
      { name: "", email: "", phone: "", isPrimary: false },
    ]);

  const handleModalContactChange = (index, field, value) => {
    setContactPersons((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
    setModalErrors((prev) => ({
      ...prev,
      [`contact_${index}`]: value.trim() ? "" : prev?.[`contact_${index}`],
    }));
  };

  const handleModalRemoveContactPerson = (index) =>
    setContactPersons((prev) => prev.filter((_, i) => i !== index));

  const handleModalPrimaryChange = (selectedIndex) =>
    setContactPersons((prev) =>
      prev.map((c, i) => ({ ...c, isPrimary: i === selectedIndex }))
    );

  // ── address handler ──────────────────────────────────────────────────────────
  const handleModalAddressChange = (field, value) => {
    setModalAddress((prev) => ({ ...prev, [field]: value }));
    setModalErrors((prev) => ({
      ...prev,
      address: Object.values({ ...modalAddress, [field]: value }).every((v) =>
        v.trim()
      )
        ? ""
        : prev.address,
    }));
  };

  // ── balance handler ──────────────────────────────────────────────────────────
  const handleModalBalanceChange = (selected) => {
    setModalSelectedOptionBalance(selected);
    setModalErrors((prev) => ({ ...prev, openingAmount: "" }));
  };

  // ── PAN handler ───────────────────────────────────────────────────────────────
  const handleModalPanChange = (e) => {
    const value = e.target.value.toUpperCase();
    setModalPan(value);
    if (modalErrors.pan)
      setModalErrors((prev) => ({ ...prev, pan: "" }));
  };

  // ── family group ─────────────────────────────────────────────────────────────
  const resetCreateFamilyModal = () => {
    setCreateFamilyForm({
      family_name: "",
      contact_name: "",
      phone_number: "",
      email: "",
    });
    setCreateFamilyErrors({});
    setIsCreatingFamily(false);
  };

  const openCreateFamilyModal = () => {
    document.activeElement?.blur?.();
    resetCreateFamilyModal();
    setIsCreateFamilyModalOpen(true);
  };

  const closeCreateFamilyModal = () => {
    setIsCreateFamilyModalOpen(false);
    resetCreateFamilyModal();
  };

  const validateCreateFamilyForm = () => {
    const nextErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!createFamilyForm.family_name.trim())
      nextErrors.family_name = "Family Name is required";
    if (!createFamilyForm.contact_name.trim())
      nextErrors.contact_name = "Contact Name is required";
    if (!createFamilyForm.phone_number.trim())
      nextErrors.phone_number = "Phone Number is required";
    else if (!/^\d{10}$/.test(createFamilyForm.phone_number.trim()))
      nextErrors.phone_number = "Phone Number must be 10 digits";
    if (!createFamilyForm.email.trim())
      nextErrors.email = "Email Id is required";
    else if (!emailRegex.test(createFamilyForm.email.trim()))
      nextErrors.email = "Enter a valid email address";
    setCreateFamilyErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCreateFamily = async () => {
    if (isCreatingFamily || !validateCreateFamilyForm()) return;
    const payload = {
      family_name: createFamilyForm.family_name.trim(),
      contact_name: createFamilyForm.contact_name.trim(),
      phone_number: createFamilyForm.phone_number.trim(),
      email: createFamilyForm.email.trim(),
    };
    try {
      setIsCreatingFamily(true);
      const response = await axiosInstance.post(
        "/api/create_family_group",
        payload
      );
      const returnedFamilyId =
        response.data?.family_id ||
        response.data?.data?.family_id ||
        response.data?.result?.family_id ||
        "";
      if (!returnedFamilyId) throw new Error("Family ID missing in response");

      const createdFamily = { family_id: returnedFamilyId, ...payload };
      setFamilyGroups((prev) => [
        ...prev.filter((f) => f.family_id !== createdFamily.family_id),
        createdFamily,
      ]);
      setSelectedFamilyId(createdFamily.family_id);
      mapFamilyContactToPrimary(createdFamily);
      setSnackbarSeverity("success");
      setSnackbarMessage(
        response.data?.message || "Family Group added successfully"
      );
      setSnackbarOpen(true);
      closeCreateFamilyModal();
    } catch (error) {
      console.error("Error creating family group:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to create Family Group"
      );
      setSnackbarOpen(true);
    } finally {
      setIsCreatingFamily(false);
    }
  };

  // ── form validation ───────────────────────────────────────────────────────────
  const validateModalForm = () => {
    let isValid = true;
    const newErrors = {};

    contactPersons.forEach((contact, i) => {
      if (!contact.name?.trim()) {
        newErrors[`name_${i}`] = "Name is required";
        isValid = false;
      } else if (contact.name.length > 100) {
        newErrors[`name_${i}`] = "Name must not exceed 100 characters";
        isValid = false;
      }
      if (!contact.email?.trim()) {
        newErrors[`email_${i}`] = "Email is required";
        isValid = false;
      } else if (
        !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
          contact.email
        )
      ) {
        newErrors[`email_${i}`] = "Enter a valid email address";
        isValid = false;
      } else if (contact.email.length > 100) {
        newErrors[`email_${i}`] = "Email must not exceed 100 characters";
        isValid = false;
      }
      if (!contact.phone?.trim()) {
        newErrors[`phone_${i}`] = "Phone number is required";
        isValid = false;
      } else if (!/^\d{10}$/.test(contact.phone)) {
        newErrors[`phone_${i}`] = "Phone number must be 10 digits";
        isValid = false;
      }
    });

    if (!modalClientName?.trim()) {
      newErrors.clientName = "Client name is required";
      isValid = false;
    } else if (modalClientName.length > 100) {
      newErrors.clientName = "Client name must not exceed 100 characters";
      isValid = false;
    }

    if (!modalCompanyName?.trim()) {
      newErrors.companyName = "Company name is required";
      isValid = false;
    } else if (modalCompanyName.length > 150) {
      newErrors.companyName = "Company name must not exceed 150 characters";
      isValid = false;
    }

    if (!modalFileNumber?.trim()) {
      newErrors.fileNumber = "File number is required";
      isValid = false;
    } else if (modalFileNumber.length > 50) {
      newErrors.fileNumber = "File number must not exceed 50 characters";
      isValid = false;
    }

    if (!modalSelectedCompanyType) {
      newErrors.companyType = "Company type is required";
      isValid = false;
    }

    if (!modalSelectedSubsidary) {
      newErrors.subsidiary = "Subsidiary selection is required";
      isValid = false;
    }

    if (
      modalSelectedOptionBalance?.value !== "Financially Clear" &&
      modalSelectedOptionBalance?.value
    ) {
      if (!modalOpeningAmount) {
        newErrors.openingAmount = "Opening amount is required";
        isValid = false;
      } else if (Number(modalOpeningAmount) < 0) {
        newErrors.openingAmount = "Opening amount must be a positive number";
        isValid = false;
      } else if (modalOpeningAmount.toString().length > 15) {
        newErrors.openingAmount = "Opening amount must not exceed 15 digits";
        isValid = false;
      }
    }

    if (!modalDob) {
      newErrors.dob = "Birth date/Date Of Incorporation is required";
      isValid = false;
    } else {
      const { error } = normalizeBirthDateInput(modalDob, {
        allowPartial: false,
      });
      if (error) {
        newErrors.dob = error;
        isValid = false;
      }
    }

    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!modalPan?.trim()) {
      newErrors.pan = "PAN number is required";
      isValid = false;
    } else if (!panRegex.test(modalPan)) {
      newErrors.pan = "Invalid PAN format (e.g., ABCDE1234F)";
      isValid = false;
    } else if (modalPan.length > 10) {
      newErrors.pan = "PAN cannot exceed 10 characters";
      isValid = false;
    }

    if (!modalAddress.street?.trim()) {
      newErrors.street = "Office number is required";
      isValid = false;
    } else if (modalAddress.street.length > 100) {
      newErrors.street = "Office number must not exceed 100 characters";
      isValid = false;
    }

    if (!modalAddress.city?.trim()) {
      newErrors.city = "Name of premises is required";
      isValid = false;
    } else if (modalAddress.city.length > 100) {
      newErrors.city = "Name of premises must not exceed 100 characters";
      isValid = false;
    }

    if (!modalAddress.state?.trim()) {
      newErrors.area = "Area is required";
      isValid = false;
    } else if (modalAddress.state.length > 100) {
      newErrors.area = "Area must not exceed 100 characters";
      isValid = false;
    }

    if (!modalAddress.postalCode) {
      newErrors.postalCode = "Postal code is required";
      isValid = false;
    } else if (!/^\d{6}$/.test(modalAddress.postalCode)) {
      newErrors.postalCode = "Enter a valid 6-digit postal code";
      isValid = false;
    }

    if (!String(modalPaymentTerm).trim()) {
      newErrors.paymentTerm = "Payment term is required";
      isValid = false;
    }

    setModalErrors(newErrors);
    return isValid;
  };

  // ── submit ───────────────────────────────────────────────────────────────────
  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (!validateModalForm() || modalLoading || modalIsSaved) return;
    if (clientType === "family group" && !selectedFamilyId) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Please select a Family Group");
      setSnackbarOpen(true);
      return;
    }

    setModalLoading(true);

    const selectedFamily =
      clientType === "family group"
        ? familyGroups.find((f) => f.family_id === selectedFamilyId)
        : null;

    let formattedDate = null;
    if (modalDob) {
      const match = modalDob.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (match)
        formattedDate = `${match[3]}-${match[2]}-${match[1]}`;
    }

    const hasPrimary = contactPersons.some((c) => c.isPrimary);
    const nonFamilyContacts = contactPersons.filter(
      (c) => !c?.isFamilyPrimaryMapped
    );

    const contactsPayload =
      clientType === "family group"
        ? [
            {
              contact_name: selectedFamily?.contact_name || "",
              contact_email: selectedFamily?.email || "",
              phone_number: (selectedFamily?.phone_number || "").toString(),
              is_primary: true,
            },
            ...nonFamilyContacts.map((c) => ({
              contact_name: c.name,
              contact_email: c.email,
              phone_number: c.phone,
              is_primary: false,
            })),
          ]
        : contactPersons.map((c, i) => ({
            contact_name: c.name,
            contact_email: c.email,
            phone_number: c.phone,
            is_primary: hasPrimary ? c.isPrimary : i === 0,
          }));

    const today = new Date().toISOString().split("T")[0];

    const formData = {
      client_name: modalClientName,
      clients_company_name: modalCompanyName,
      file_number: modalFileNumber,
      company_type: modalSelectedCompanyType || "",
      contacts: contactsPayload,
      ...(clientType === "family group"
        ? { client_type: "family group", family_id: selectedFamilyId }
        : { client_type: "individual" }),
      sub_id: modalSelectedSubsidary,
      payment_terms: modalPaymentTerm,
      dob: formattedDate,
      pan_number: modalPan,
      office_number: modalAddress.street,
      landmark: modalAddress.city,
      area: modalAddress.state,
      pin: modalAddress.postalCode,
      balance_type: modalSelectedOptionBalance?.label,
      opening_balance: modalOpeningAmount,
      date: formattedDate || today,
    };

    try {
      const response = await axiosInstance.post(`/api/create_client`, formData);

      if (response.status === 200) {
        setSnackbarSeverity("success");
        setSnackbarMessage("Client added successfully!");
        setSnackbarOpen(true);
        setModalIsSaved(true);

        const createdClient = response.data?.data || response.data;
        const newClientId = createdClient?.client_id;

        if (createdClient && setClients) {
          setClients((prev) => [
            ...prev,
            {
              client_id: createdClient.client_id,
              client_name: createdClient.client_name,
              company_type: createdClient.company_type,
              terms: createdClient.payment_terms,
              subsidiary_id: createdClient.subsidiary_id,
            },
          ]);
        }

        if (newClientId && onClientCreated) {
          onClientCreated(newClientId);
        }

        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      if (error.response?.data?.error === "PAN number already exists") {
        setModalErrors((prev) => ({
          ...prev,
          pan: "This PAN number is already registered in the system",
        }));
        setSnackbarSeverity("error");
        setSnackbarMessage("PAN number already exists in the system");
      } else {
        setSnackbarSeverity("error");
        setSnackbarMessage(
          error.response?.data?.error || error.response?.data?.message
        );
      }
      setSnackbarOpen(true);
    } finally {
      setModalLoading(false);
      setIsModalSubmitting(false);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────────
  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        disableEnforceFocus={isCreateFamilyModalOpen}
        disableAutoFocus={isCreateFamilyModalOpen}
        disableRestoreFocus={isCreateFamilyModalOpen}
        aria-labelledby="client-modal-title"
        aria-describedby="client-modal-description"
        sx={{
          zIndex: 1300,
          "& .MuiBackdrop-root": { zIndex: 1299 },
        }}
      >
        <Box sx={modalStyle}>
          {/* Header */}
          <Box sx={modalHeaderStyle}>
            <Typography id="client-modal-title" variant="h6" component="h2">
              New Client Management
            </Typography>
            <IconButton onClick={handleClose} size="small" sx={{ color: "grey.500" }}>
              <X />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={modalContentStyle}>
            <form onSubmit={handleModalSubmit} className="client-modal-form">

              {/* Client Name */}
              <div className="client-form-group" style={{ marginBottom: "20px" }}>
                <label className="client-form-label">
                  Client Name <span className="client-required">*</span>
                </label>
                <input
                  ref={firstfield}
                  type="text"
                  className="client-input-field"
                  placeholder="Enter Client Name"
                  value={modalClientName}
                  maxLength={100}
                  onChange={(e) => {
                    setModalClientName(e.target.value);
                    if (modalErrors.clientName)
                      setModalErrors((p) => ({ ...p, clientName: "" }));
                  }}
                />
                {modalErrors.clientName && (
                  <p className="client-error-message">{modalErrors.clientName}</p>
                )}
              </div>

              {/* Company Name */}
              <div className="client-form-group" style={{ marginBottom: "20px" }}>
                <label className="client-form-label">
                  Company/Firm Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="client-input-field"
                  placeholder="Enter Company/Firm Name"
                  value={modalCompanyName}
                  maxLength={150}
                  onChange={(e) => {
                    setModalCompanyName(e.target.value);
                    if (modalErrors.companyName)
                      setModalErrors((p) => ({ ...p, companyName: "" }));
                  }}
                />
                {modalErrors.companyName && (
                  <p className="client-error-message">{modalErrors.companyName}</p>
                )}
              </div>

              {/* File Number */}
              <div className="client-form-group" style={{ marginBottom: "20px" }}>
                <label className="client-form-label">
                  File Number <span className="client-required">*</span>
                </label>
                <input
                  type="text"
                  className="client-input-field"
                  placeholder="Enter File Number"
                  value={modalFileNumber}
                  onChange={(e) => {
                    setModalFileNumber(e.target.value);
                    if (modalErrors.fileNumber)
                      setModalErrors((p) => ({ ...p, fileNumber: "" }));
                  }}
                />
                {modalErrors.fileNumber && (
                  <p className="client-error-message">{modalErrors.fileNumber}</p>
                )}
              </div>

              {/* Company Type */}
              <div className="client-form-group" style={{ marginBottom: "20px" }}>
                <label className="client-form-label">
                  Company Type <span className="client-required">*</span>
                </label>
                <Select
                  value={
                    modalSelectedCompanyType
                      ? companyTypes.find(
                          (o) => o.value === modalSelectedCompanyType
                        )
                      : null
                  }
                  onChange={(o) => {
                    setModalSelectedCompanyType(o ? o.value : "");
                    if (modalErrors.companyType)
                      setModalErrors((p) => ({ ...p, companyType: "" }));
                  }}
                  options={companyTypes}
                  placeholder="Select Company Type"
                  isClearable
                  styles={{
                    menuPortal: (b) => ({ ...b, zIndex: 1500 }),
                    menu: (b) => ({ ...b, zIndex: 1500 }),
                  }}
                  menuPortalTarget={document.body}
                />
                {modalErrors.companyType && (
                  <p className="client-error-message">{modalErrors.companyType}</p>
                )}
              </div>

              {/* Opening Balance */}
              <div className="client-form-group" style={{ marginBottom: "20px" }}>
                <label className="client-form-label">
                  Opening Balance <span className="client-required">*</span>
                </label>
                <Select
                  value={modalSelectedOptionBalance}
                  options={balanceOptions}
                  placeholder="Select advance/pending"
                  isClearable
                  onChange={handleModalBalanceChange}
                  styles={{
                    menuPortal: (b) => ({ ...b, zIndex: 1500 }),
                    menu: (b) => ({ ...b, zIndex: 1500 }),
                  }}
                  menuPortalTarget={document.body}
                />

                {/* Client Type radio */}
                <div
                  className="form-group"
                  style={{ flexDirection: "column", marginBottom: "0px" }}
                >
                  <label className="form-label">
                    Client Type <span className="required">*</span>
                  </label>
                  <div className="form-inputs">
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="client_type"
                          checked={clientType === "individual"}
                          onChange={() => handleClientTypeChange("individual")}
                        />
                        <span className="radio-text">Individual</span>
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="client_type"
                          checked={clientType === "family group"}
                          onChange={() => handleClientTypeChange("family group")}
                        />
                        <span className="radio-text">Family Group</span>
                      </label>
                    </div>
                    {errors.clientType && (
                      <p className="error-message">{errors.clientType}</p>
                    )}
                  </div>
                </div>

                {/* Family Group selector */}
                {clientType === "family group" && (
                  <div className="form-group">
                    <label className="form-label">Select Family Group</label>
                    <div className="form-inputs form-family-row">
                      <Select
                        className="select-field"
                        value={
                          selectedFamilyId
                            ? familyGroups
                                .map((f) => ({
                                  value: f.family_id,
                                  label: f.family_name,
                                }))
                                .find((o) => o.value === selectedFamilyId) ||
                              null
                            : null
                        }
                        onChange={(o) => {
                          const nextId = o?.value || "";
                          setSelectedFamilyId(nextId);
                          if (!nextId) {
                            setContactPersons((prev) =>
                              clearMappedFamilyContact(prev)
                            );
                            return;
                          }
                          mapFamilyContactToPrimary(
                            familyGroups.find((f) => f.family_id === nextId)
                          );
                        }}
                        options={familyGroups.map((f) => ({
                          value: f.family_id,
                          label: f.family_name,
                        }))}
                        placeholder={
                          familyGroupsLoading
                            ? "Loading family groups..."
                            : "Select Family Group"
                        }
                        isClearable
                        isLoading={familyGroupsLoading}
                        menuPortalTarget={menuPortalTarget}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                          menu: (b) => ({ ...b, zIndex: 9999 }),
                        }}
                      />
                      <button
                        type="button"
                        className="family-plus-btn"
                        aria-label="Add family group"
                        onClick={openCreateFamilyModal}
                      >
                        +
                      </button>
                      {familyGroupsError && (
                        <p className="error-message">{familyGroupsError}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Opening amount input */}
                {modalSelectedOptionBalance &&
                  modalSelectedOptionBalance.value !== "Financially Clear" && (
                    <input
                      type="number"
                      className="client-input-field"
                      style={{ marginTop: "10px" }}
                      placeholder="Enter Amount"
                      value={modalOpeningAmount}
                      maxLength={15}
                      onChange={(e) => {
                        setModalOpeningAmount(e.target.value);
                        if (modalErrors.openingAmount)
                          setModalErrors((p) => ({ ...p, openingAmount: "" }));
                      }}
                    />
                  )}
                {modalSelectedOptionBalance?.value !== "Financially Clear" &&
                  modalSelectedOptionBalance?.value &&
                  modalErrors.openingAmount && (
                    <p className="client-error-message">
                      {modalErrors.openingAmount}
                    </p>
                  )}
              </div>

              {/* DOB + PAN */}
              <div className="client-form-group" style={{ marginBottom: "20px" }}>
                <label className="client-form-label">
                  Client Info <span className="required">*</span>
                </label>
                <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: "50%" }}>
                    <DatePicker
                      className="client-input-field"
                      selected={
                        modalDob
                          ? (() => {
                              const [d, m, y] = modalDob.split("-");
                              return new Date(y, m - 1, d);
                            })()
                          : null
                      }
                      onChange={(date) => {
                        if (!date) { setModalDob(""); return; }
                        const d = String(date.getDate()).padStart(2, "0");
                        const m = String(date.getMonth() + 1).padStart(2, "0");
                        const y = date.getFullYear();
                        const formatted = `${d}-${m}-${y}`;
                        setModalDob(formatted);
                        const { error } = normalizeBirthDateInput(formatted, {
                          allowPartial: false,
                        });
                        setModalDobError(error);
                        if (modalErrors.dob)
                          setModalErrors((p) => ({ ...p, dob: "" }));
                      }}
                      dateFormat="dd-MM-yyyy"
                      placeholderText="DD-MM-YYYY"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                    />
                    {modalErrors.dob && (
                      <p
                        className="client-error-message"
                        style={{ marginTop: "5px", color: "red", fontSize: "12px" }}
                      >
                        {modalErrors.dob}
                      </p>
                    )}
                    <small className="date-format-hint">
                      Enter Birth Date/Date Of Incorporation
                    </small>
                  </div>
                  <div style={{ flex: 1, minWidth: "200px" }}>
                    <input
                      type="text"
                      className="client-input-field"
                      placeholder="Enter PAN Number"
                      value={modalPan}
                      maxLength={10}
                      onChange={handleModalPanChange}
                    />
                    {modalErrors.pan && (
                      <p
                        className="client-error-message"
                        style={{ marginTop: "5px", color: "red", fontSize: "12px" }}
                      >
                        {modalErrors.pan}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="client-form-group" style={{ marginBottom: "20px" }}>
                <label className="client-form-label">
                  Client Address <span className="client-required">*</span>
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "15px",
                    marginBottom: "15px",
                  }}
                >
                  <div>
                    <input
                      type="text"
                      className="client-input-field"
                      placeholder="Enter office number"
                      value={modalAddress.street}
                      onChange={(e) =>
                        handleModalAddressChange("street", e.target.value)
                      }
                    />
                    {modalErrors.street && (
                      <p className="client-error-message">{modalErrors.street}</p>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      className="client-input-field"
                      placeholder="Name of premises"
                      value={modalAddress.city}
                      maxLength={100}
                      onChange={(e) => {
                        handleModalAddressChange("city", e.target.value);
                        if (modalErrors.city)
                          setModalErrors((p) => ({ ...p, city: "" }));
                      }}
                    />
                    {modalErrors.city && (
                      <p className="client-error-message">{modalErrors.city}</p>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "15px",
                  }}
                >
                  <div>
                    <input
                      type="text"
                      className="client-input-field"
                      placeholder="Area"
                      value={modalAddress.state}
                      maxLength={100}
                      onChange={(e) => {
                        handleModalAddressChange("state", e.target.value);
                        if (modalErrors.area)
                          setModalErrors((p) => ({ ...p, area: "" }));
                      }}
                    />
                    {modalErrors.area && (
                      <p className="client-error-message">{modalErrors.area}</p>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      className="client-input-field"
                      placeholder="Postal code"
                      value={modalAddress.postalCode}
                      maxLength={6}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                        handleModalAddressChange("postalCode", value);
                        if (modalErrors.postalCode)
                          setModalErrors((p) => ({ ...p, postalCode: "" }));
                      }}
                    />
                    {modalErrors.postalCode && (
                      <p className="client-error-message">
                        {modalErrors.postalCode}
                      </p>
                    )}
                  </div>
                </div>
                {modalErrors.address && (
                  <p className="client-error-message">{modalErrors.address}</p>
                )}
              </div>

              {/* Contact Persons */}
              <div className="client-form-group" style={{ marginBottom: "20px" }}>
                <label className="client-form-label">
                  Contact Person Details <span className="client-required">*</span>
                </label>
                {contactPersons.map((contact, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: "15px",
                      padding: "15px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: "15px",
                        marginBottom: "15px",
                      }}
                    >
                      <div>
                        <input
                          type="text"
                          className="client-input-field"
                          placeholder="Enter Contact Person Name"
                          value={contact.name}
                          maxLength={100}
                          onChange={(e) =>
                            handleModalContactChange(index, "name", e.target.value)
                          }
                        />
                        {modalErrors[`name_${index}`] && (
                          <p className="client-error-message">
                            {modalErrors[`name_${index}`]}
                          </p>
                        )}
                      </div>
                      <div>
                        <input
                          type="email"
                          className="client-input-field"
                          placeholder="Enter Contact Person Email"
                          value={contact.email}
                          maxLength={100}
                          onChange={(e) =>
                            handleModalContactChange(index, "email", e.target.value)
                          }
                        />
                        {modalErrors[`email_${index}`] && (
                          <p className="client-error-message">
                            {modalErrors[`email_${index}`]}
                          </p>
                        )}
                      </div>
                      <div>
                        <input
                          type="text"
                          className="client-input-field"
                          placeholder="Enter Phone Number"
                          value={contact.phone}
                          maxLength={10}
                          inputMode="numeric"
                          onChange={(e) => {
                            const value = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 10);
                            handleModalContactChange(index, "phone", value);
                          }}
                        />
                        {modalErrors[`phone_${index}`] && (
                          <p className="client-error-message">
                            {modalErrors[`phone_${index}`]}
                          </p>
                        )}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <label style={{ marginRight: "10px" }}>
                          Set as Primary Contact?
                        </label>
                        <input
                          type="radio"
                          name="primaryContact"
                          checked={contact.isPrimary ?? index === 0}
                          onChange={() => handleModalPrimaryChange(index)}
                        />
                      </div>
                      {contactPersons.length > 1 && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleModalRemoveContactPerson(index)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                    {modalErrors[`contact_${index}`] && (
                      <p className="error-message">
                        {modalErrors[`contact_${index}`]}
                      </p>
                    )}
                  </div>
                ))}
                <Button
                  variant="outlined"
                  onClick={handleModalAddContactPerson}
                  style={{ marginTop: "10px" }}
                >
                  + Add More Contact Person
                </Button>
              </div>

              {/* Subsidiary */}
              <div className="client-form-group" style={{ marginBottom: "20px" }}>
                <label className="form-label">
                  Customer Billing Company (Subsidiary){" "}
                  <span className="required">*</span>
                </label>
                <Select
                  className="select-field"
                  value={
                    modalSelectedSubsidary
                      ? modalSubsidaries.find(
                          (s) => s.value === modalSelectedSubsidary
                        )
                      : null
                  }
                  onChange={(o) => {
                    setModalSelectedSubsidary(o ? o.value : null);
                    if (modalErrors.subsidiary)
                      setModalErrors((p) => ({ ...p, subsidiary: "" }));
                  }}
                  options={modalSubsidaries.map((s) => ({
                    value: s.value,
                    label: `${s.label} (${s.tag_name})`,
                  }))}
                  placeholder="Select Type Of Client"
                  isClearable
                  isLoading={modalLoading}
                />
                {modalErrors.subsidiary && (
                  <p className="client-error-message">{modalErrors.subsidiary}</p>
                )}
              </div>

              {/* Payment Terms */}
              <div className="client-form-group" style={{ marginBottom: "20px" }}>
                <label className="client-form-label">
                  Payment Terms <span className="client-required">*</span>
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Enter Payment Terms"
                    value={modalPaymentTerm}
                    onChange={(e) => setModalPaymentTerm(e.target.value)}
                    style={{ flex: 1, minWidth: "130px" }}
                  />
                  <span>Days</span>
                </div>
                {modalErrors.paymentTerm && (
                  <p className="client-error-message">{modalErrors.paymentTerm}</p>
                )}
              </div>

              {/* Actions */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 3,
                  pt: 2,
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  color="inherit"
                  disabled={modalLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  onClick={handleModalSubmit}
                  disabled={modalLoading || modalIsSaved || isModalSubmitting}
                  sx={{ bgcolor: "#000", "&:hover": { bgcolor: "#1e1b1bff" } }}
                >
                  {isModalSubmitting ? (
                    <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <ClipLoader size={16} color="#FFF" />
                      Creating Client...
                    </span>
                  ) : modalLoading ? (
                    "Saving..."
                  ) : modalIsSaved ? (
                    "Saved"
                  ) : (
                    "Save Client"
                  )}
                </Button>
              </Box>
            </form>
          </Box>

          {/* Snackbar scoped inside modal */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            style={{ transform: "translateY(50px)", marginLeft: "20px", zIndex: 1400 }}
          >
            <MuiAlert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
              {snackbarMessage}
            </MuiAlert>
          </Snackbar>
        </Box>
      </Modal>

      {/* Create Family Group portal */}
      {isCreateFamilyModalOpen &&
        familyModalPortalTarget &&
        createPortal(
          <div className="invoice-family-modal-root">
            <div className="invoice-family-modal-overlay" />
            <div className="invoice-family-modal-wrapper">
              <div className="invoice-family-modal-content">
                <h2>Create New Family Group</h2>
                <input
                  ref={createFamilyFirstInputRef}
                  type="text"
                  className="invoice-family-input-field"
                  autoFocus
                  placeholder="Family Name *"
                  value={createFamilyForm.family_name}
                  onChange={(e) => {
                    setCreateFamilyForm((p) => ({
                      ...p,
                      family_name: e.target.value,
                    }));
                    setCreateFamilyErrors((p) => ({ ...p, family_name: "" }));
                  }}
                />
                {createFamilyErrors.family_name && (
                  <p className="error-message">{createFamilyErrors.family_name}</p>
                )}
                <input
                  type="text"
                  className="invoice-family-input-field"
                  placeholder="Contact Name *"
                  value={createFamilyForm.contact_name}
                  onChange={(e) => {
                    setCreateFamilyForm((p) => ({
                      ...p,
                      contact_name: e.target.value,
                    }));
                    setCreateFamilyErrors((p) => ({ ...p, contact_name: "" }));
                  }}
                />
                {createFamilyErrors.contact_name && (
                  <p className="error-message">{createFamilyErrors.contact_name}</p>
                )}
                <input
                  type="text"
                  className="invoice-family-input-field"
                  placeholder="Phone Number *"
                  value={createFamilyForm.phone_number}
                  maxLength={10}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setCreateFamilyForm((p) => ({ ...p, phone_number: v }));
                    setCreateFamilyErrors((p) => ({ ...p, phone_number: "" }));
                  }}
                />
                {createFamilyErrors.phone_number && (
                  <p className="error-message">{createFamilyErrors.phone_number}</p>
                )}
                <input
                  type="email"
                  className="invoice-family-input-field"
                  placeholder="Email Id *"
                  value={createFamilyForm.email}
                  onChange={(e) => {
                    setCreateFamilyForm((p) => ({
                      ...p,
                      email: e.target.value,
                    }));
                    setCreateFamilyErrors((p) => ({ ...p, email: "" }));
                  }}
                />
                {createFamilyErrors.email && (
                  <p className="error-message">{createFamilyErrors.email}</p>
                )}
                <div className="invoice-family-modal-buttons">
                  <button
                    type="button"
                    className="btn btn-cancel"
                    onClick={closeCreateFamilyModal}
                    disabled={isCreatingFamily}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-save"
                    onClick={handleCreateFamily}
                    disabled={isCreatingFamily}
                  >
                    {isCreatingFamily ? "Creating..." : "Create"}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          familyModalPortalTarget
        )}
    </>
  );
}
